'use client';

import { fromRow } from '@slims/shared/data';
import type { Member } from '@slims/shared/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';

interface UseMembersReturn {
	members: Member[];
	isLoading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
	addMember: (data: Omit<Member, 'id' | 'tenantId'>) => Promise<Member>;
	updateMember: (id: string, data: Partial<Member>) => Promise<Member>;
	deleteMember: (id: string) => Promise<void>;
	inviteMember: (
		memberId: string,
	) => Promise<{ member: Member; inviteLink: string | null; emailSent: boolean }>;
	associateUser: (
		memberId: string,
		body: { userId?: string; email?: string; password?: string; fullName?: string },
	) => Promise<Member>;
}

const EMPTY: Member[] = [];

/** React Query key for the members of one org. Exported so other code can invalidate it. */
export const membersKey = (orgId: string | null | undefined) => ['members', orgId] as const;

/** Fetcher for an org's members — shared by the hook and by hover-prefetch. */
export const fetchMembers = (orgId: string) => api.get<Member[]>(`/api/members?orgId=${orgId}`);

/**
 * Members of a single org. `orgId` is required — pass the panel scope (view
 * pages) or the admin scope configId (admin pages); null renders empty while
 * the scope is still resolving.
 *
 * ADR-006: backed by React Query, so the list is cached and deduped across the
 * N cards of a «Panoramica» and across navigations. The `members` realtime
 * channel patches the cached list in place; mutations update the cache directly
 * (no refetch).
 */
export function useMembers(orgId: string | null | undefined): UseMembersReturn {
	const queryClient = useQueryClient();
	const { leaves } = useScopeTree();

	const tenantId = useMemo(
		() => leaves.find((l) => l.orgId === orgId)?.tenantId ?? null,
		[leaves, orgId],
	);
	const orgParam = orgId ? `?orgId=${orgId}` : '';

	const {
		data,
		isLoading,
		error,
		refetch: rqRefetch,
	} = useQuery({
		queryKey: membersKey(orgId),
		enabled: !!orgId,
		queryFn: () => fetchMembers(orgId as string),
	});

	const members = data ?? EMPTY;

	const patchCache = useCallback(
		(update: (prev: Member[]) => Member[]) => {
			queryClient.setQueryData<Member[]>(membersKey(orgId), (prev) => update(prev ?? []));
		},
		[queryClient, orgId],
	);

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	// Supabase Realtime subscription — scoped to the org's tenant.
	useEffect(() => {
		if (!tenantId) return;

		return subscribeToTable(
			`members-realtime-${tenantId}`,
			{
				event: '*',
				schema: 'public',
				table: 'members',
				filter: `tenant_id=eq.${tenantId}`,
			},
			(payload) => {
				// Postgres consegna la riga in snake_case: senza `fromRow` il socio in
				// lista perdeva `dataIscrizione`, `hasStorage`… fino al reload, e la
				// ricerca andava in errore sui campi undefined
				// (INB-126).
				if (payload.eventType === 'INSERT') {
					const inserted = fromRow('members', payload.new) as unknown as Member;
					patchCache((prev) =>
						prev.some((m) => m.id === inserted.id) ? prev : [...prev, inserted],
					);
				} else if (payload.eventType === 'UPDATE') {
					const updated = fromRow('members', payload.new) as unknown as Member;
					// Merge, non sostituzione: `accesso` lo calcola la GET dell'elenco e
					// la riga di Postgres non ce l'ha.
					patchCache((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
				} else if (payload.eventType === 'DELETE') {
					const removedId = (payload.old as { id: string }).id;
					patchCache((prev) => prev.filter((m) => m.id !== removedId));
				}
			},
		);
	}, [tenantId, patchCache]);

	const addMember = useCallback(
		async (data: Omit<Member, 'id' | 'tenantId'>) => {
			const newMember = await api.post<Member>(`/api/members${orgParam}`, data);
			patchCache((prev) => (prev.some((m) => m.id === newMember.id) ? prev : [...prev, newMember]));
			return newMember;
		},
		[orgParam, patchCache],
	);

	const updateMember = useCallback(
		async (id: string, data: Partial<Member>) => {
			const updated = await api.patch<Member>(`/api/members/${id}${orgParam}`, data);
			patchCache((prev) => prev.map((m) => (m.id === id ? { ...m, ...updated } : m)));
			return updated;
		},
		[orgParam, patchCache],
	);

	const deleteMember = useCallback(
		async (id: string) => {
			await api.delete(`/api/members/${id}${orgParam}`);
			patchCache((prev) => prev.filter((m) => m.id !== id));
		},
		[orgParam, patchCache],
	);

	const associateUser = useCallback(
		async (
			memberId: string,
			body: { userId?: string; email?: string; password?: string; fullName?: string },
		) => {
			const updated = await api.post<Member>(`/api/members/${memberId}/user${orgParam}`, body);
			patchCache((prev) => prev.map((m) => (m.id === memberId ? { ...m, ...updated } : m)));
			return updated;
		},
		[orgParam, patchCache],
	);

	const inviteMember = useCallback(
		async (memberId: string) => {
			const result = await api.post<{
				member: Member;
				inviteLink: string | null;
				emailSent: boolean;
			}>(`/api/members/invite${orgParam}`, { memberId });
			patchCache((prev) => prev.map((m) => (m.id === memberId ? result.member : m)));
			return result;
		},
		[orgParam, patchCache],
	);

	return {
		members,
		isLoading: orgId ? isLoading : true,
		error: error ? (error instanceof Error ? error.message : 'Errore nel caricamento dei soci') : null,
		refetch,
		addMember,
		updateMember,
		deleteMember,
		inviteMember,
		associateUser,
	};
}
